'use client';

import React, { useRef, useState } from 'react';

import { cn } from '@/lib/utils';
import { useReducedMotion, useSpring } from 'framer-motion';

import Chip from '@/components/global/ui/Chip';
import DecorationLine from '@/components/global/ui/DecorationLine';
import { MotionDiv, MotionP } from '@/components/global/utils/motionTags';
import { EASE_OUT } from '@/components/global/utils/motionTiming';

import { resolvePageLink } from '@/sanity/globals/CTA';
import { PageCareerSchema } from '@/sanity/pages/PageCareerTemplate';
import DotsBackgroundWhite from './DotsBackgroundWhite';

/*
  Trailing spring for the card. Deliberately not PARALLAX_SPRING: that one is
  tuned to lag well behind scroll, which on a cursor reads as the card being
  dragged on a string. This is stiff enough to feel attached to the pointer and
  still damped past oscillation, so it settles rather than wobbles.
*/
const CURSOR_SPRING = {
  stiffness: 300,
  damping: 30,
  mass: 0.5,
  restDelta: 0.5,
} as const;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), Math.max(min, max));

type Position = PageCareerSchema['positionsItems'][number];

function PositionCard({
  item,
  linkLabel,
  className,
}: {
  item: Position;
  linkLabel?: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'border-t-[0.375rem] border-[#ABF364] bg-white px-[2.5rem] pb-[2.5rem] pt-[2.75rem]',
        className,
      )}
    >
      <div className="flex flex-col gap-[6.25rem]">
        <div className="flex flex-col gap-[2.25rem]">
          <div className="flex flex-col gap-[0.75rem]">
            {item?.cardTitle && (
              <h3 className="mb-0 font-geist !text-[2.5rem] font-medium leading-[1.5] text-black lg:!text-[2.25rem]">
                {item.cardTitle}
              </h3>
            )}

            {item?.text && (
              /*
                The hover card clamps the blurb to three lines so every card is the
                same height. Stacked on mobile there is nothing to line up against,
                and the design lets the copy run — so the clamp is desktop-only.
              */
              <p className="mb-0 font-geist text-[2rem] leading-[1.5] text-black/60 lg:line-clamp-3">
                {item.text}
              </p>
            )}
          </div>

          {item?.tags && item.tags.length > 0 && (
            <div className="flex flex-wrap gap-[0.75rem]">
              {item.tags.map((tag, index) => (
                <span
                  key={index}
                  className="flex items-center justify-center bg-[#D9F9B9] px-[1rem] py-[0.5rem] font-geist text-[2rem] leading-[1.5] text-black [text-box-edge:cap_alphabetic] [text-box-trim:trim-both]"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>

        {linkLabel && (
          <div className="flex min-h-[5.875rem] items-center justify-center gap-[1.25rem] bg-[#F3F2F1] p-[1.25rem]">
            <p className="mb-0 font-geist text-[2.25rem] font-medium leading-none text-[#112628] underline [text-box-edge:cap_alphabetic] [text-box-trim:trim-both]">
              {linkLabel} <span aria-hidden="true">→</span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function PositionsSection({
  data,
  className,
}: {
  data: PageCareerSchema;
  className?: string;
}) {
  const items = data?.positionsItems;
  const [active, setActive] = useState<number | null>(null);
  // keeps the card's content while it fades out, so it never blanks mid-transition
  const [shown, setShown] = useState(0);
  const cardRef = useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion();

  const x = useSpring(0, CURSOR_SPRING);
  const y = useSpring(0, CURSOR_SPRING);

  /*
    The card is anchored to the pointer rather than to the row, and clamped to the
    list so it never hangs outside the section. Its left edge sits on the cursor
    and it is centred on it vertically; `pointer-events-none` keeps it from ever
    stealing the hover from the row underneath it.
  */
  const followCursor = (event: React.MouseEvent<HTMLDivElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const card = cardRef.current;
    const cardWidth = card?.offsetWidth ?? 0;
    const cardHeight = card?.offsetHeight ?? 0;

    const nextX = clamp(
      event.clientX - bounds.left,
      0,
      bounds.width - cardWidth,
    );
    const nextY = clamp(
      event.clientY - bounds.top - cardHeight / 2,
      0,
      bounds.height - cardHeight,
    );

    if (reduceMotion) {
      x.jump(nextX);
      y.jump(nextY);
      return;
    }

    x.set(nextX);
    y.set(nextY);
  };

  if (!items?.length) return null;

  const language = data?.settings?.language;

  return (
    <section
      className={cn(
        'relative overflow-clip pb-[6rem] pt-[6rem] sm:pb-[8.5rem] sm:pt-[12.125rem]',
        className,
      )}
    >
      <DotsBackgroundWhite className="absolute top-0 left-1/2 -translate-x-1/2" />
      {/* the page rails, spanning the section's full height */}
      <DecorationLine
        className="top-0"
        orientation="vertical"
        position="left"
      />
      <DecorationLine
        className="top-0"
        orientation="vertical"
        position="right"
      />

      <div className="relative mx-auto w-[min(225.375rem,100%)] px-[2.5rem] sm:px-[3rem] md:px-[5rem] lg:px-0">
        {data?.positionsChip?.text && (
          <Chip
            className="mb-0 flex justify-center [&_p]:font-geistmono [&_p]:uppercase [&_p]:text-[#202223]"
            image={data?.positionsChip?.icon}
            text={data?.positionsChip?.text}
          />
        )}

        <div
          className="relative mt-[4.25rem] flex flex-col gap-[2rem] sm:mt-[8rem] lg:block"
          onMouseMove={followCursor}
          onMouseLeave={() => setActive(null)}
        >
          <DecorationLine
            className="top-0 hidden lg:block"
            orientation="horizontal"
            style="dark"
          />

          {items.map((item, index) => {
            const href = resolvePageLink(item?.link ?? null, language) ?? '/';
            const isDimmed = active !== null && active !== index;

            return (
              <a
                key={index}
                href={href}
                target={item?.link?.openInNewTab ? '_blank' : undefined}
                rel={
                  item?.link?.openInNewTab ? 'noopener noreferrer' : undefined
                }
                onMouseEnter={() => {
                  setActive(index);
                  setShown(index);
                }}
                onFocus={() => {
                  setActive(index);
                  setShown(index);
                }}
                onBlur={() => setActive(null)}
                className="relative flex flex-col no-underline lg:h-[26rem] lg:flex-row lg:items-center lg:justify-between"
              >
                {/*
                  Number tab — bottom-left corner cut, number sitting high in it, both
                  as drawn. It belongs to the big-title row, which mobile drops entirely.
                */}
                <div className="relative hidden h-[6rem] w-[4.5rem] shrink-0 bg-[#D9F9B9] [clip-path:polygon(0_0,100%_0,100%_100%,1.54rem_100%,0_calc(100%-1.46rem))] lg:block">
                  <div className="absolute inset-x-0 top-[1.75rem] flex justify-center">
                    <span className="font-geistmono text-[2rem] font-medium uppercase leading-none text-black [text-box-edge:cap_alphabetic] [text-box-trim:trim-both]">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                  </div>
                </div>

                <MotionP
                  className="mb-0 hidden text-center font-funnel font-medium leading-none text-[#0A1A1D] lg:block lg:flex-1 lg:text-[12.5rem]"
                  animate={{ opacity: isDimmed ? 0.2 : 1 }}
                  transition={{
                    duration: reduceMotion ? 0 : 0.35,
                    ease: EASE_OUT,
                  }}
                >
                  {item?.title}
                </MotionP>

                {/* mirrors the tab so the title stays optically centred */}
                <div
                  className="hidden h-[6rem] w-[4.5rem] shrink-0 lg:block"
                  aria-hidden="true"
                />

                {/*
                  Touch has no hover, so below lg the row *is* the card — the design
                  drops the tab, the oversized title and the row rules with it.
                */}
                <PositionCard
                  className="lg:hidden"
                  item={item}
                  linkLabel={data?.positionsLinkLabel}
                />

                <DecorationLine
                  className="bottom-0 hidden lg:block"
                  orientation="horizontal"
                  style="dark"
                />
              </a>
            );
          })}

          {/*
            Always mounted rather than swapped in on hover: the follow maths needs
            the card's measured size from the very first pointer move, and keeping
            one instance alive is what lets it travel between rows instead of
            cross-fading in place.
          */}
          <MotionDiv
            className="pointer-events-none absolute left-0 top-0 z-10 hidden w-[48.625rem] lg:block"
            style={{ x, y }}
            animate={{
              opacity: active !== null ? 1 : 0,
              scale: active !== null ? 1 : 0.98,
            }}
            initial={false}
            transition={{ duration: reduceMotion ? 0 : 0.3, ease: EASE_OUT }}
          >
            <div ref={cardRef}>
              <PositionCard
                item={items[shown]}
                linkLabel={data?.positionsLinkLabel}
              />
            </div>
          </MotionDiv>
        </div>
      </div>
    </section>
  );
}
